import React, { useEffect } from 'react';
import './Picker.scss';
import { Dropdown, TextField, DatePicker } from '@fluentui/react';
import { pickerDropdownStyles, pickerTextFieldStyles } from '../../helpers/helpers';
import ProductDevicePicker from './pickerHelpers/ProductDevicePicker';
import AssignedToPicker from './pickerHelpers/AssignedToPicker';
import TagsPicker from './pickerHelpers/TagsPicker';
import AgingPicker from './pickerHelpers/AgingPicker';
import CountryPicker from './pickerHelpers/CountryPicker';
import NumberFormat, { NumberFormatValues } from 'react-number-format';
import { ListBuilderCondition } from 'app/enums/ListBuilderCondition.enum';
import { StringConstants } from 'app/utils/constants';
import { EnumOption } from '../FieldBuilder';
import { IOptionProps } from 'app/modules/admin/manageRequests/models';
import { Aging, GraphUserDto } from 'generated/request-backend';
import { IUserProps } from 'app/models/common/response';
import { SubmitterDto } from 'generated/user-backend';

interface IProps {
    picker?: EnumOption[];
    setPickerSelection?: (selection: EnumOption[]) => void;
    fieldOption?: string;
    setPickerOption?: (option: string | number | Date | string[] | boolean) => void;
    pickerOption?: string | number | Date | string[] | boolean;
    selectedKeys: (string | number | IOptionProps | IUserProps)[];
    setSelectedKeys: (keys: (string | number | IOptionProps | IUserProps)[]) => void;
    selectedOptions: (string | number | IOptionProps)[];
    setSelectedOptions: (options: (string | number | IOptionProps)[]) => void;
    inEditMode?: boolean;
    multiSelectData?: (string | number | Aging | GraphUserDto | SubmitterDto)[];
    pickerDropdownDisabled: boolean;
    pickerActive: boolean;
    conditionSelection: IOptionProps;
}

const Picker: React.FC<IProps> = ({
    picker,
    //setPickerSelection,
    fieldOption,
    pickerOption,
    setPickerOption,
    setSelectedOptions,
    setSelectedKeys,
    selectedKeys,
    selectedOptions,
    inEditMode,
    multiSelectData,
    pickerDropdownDisabled,
    pickerActive,
    conditionSelection
}) => {

    const onChange = React.useCallback((event: React.FormEvent<HTMLDivElement>, option?: EnumOption, index?: number): void => {
        setPickerOption(option.enumValue);
        //commenting out for now per Andrew
        //setPickerSelection([{key: option.key, text: option.text, value: option.enumValue }])
    }, []);

    const onTextDropdownChange = (event: React.FormEvent<HTMLDivElement>, option?: IOptionProps, index?: number): void => {
        setPickerOption(option.text);
        //commenting out for now per Andrew
        // setPickerSelection([{key: option.key, text: option.text, value: option.enumValue}]);
    };

    const onTextfieldChange = React.useCallback(
        (event: React.FormEvent<HTMLInputElement | HTMLTextAreaElement>, newValue?: string): void => {
            if (newValue.length > 0) {
                setPickerOption(newValue);
            } else {
                setPickerOption(null);
            }
        },
        []
    );

    const onMultiSelectChange = (event: React.FormEvent<HTMLDivElement>, item: EnumOption) => {
        if (item.selected) {
            setSelectedKeys([...selectedKeys, item.key]);
            setSelectedOptions([...selectedOptions, item.enumValue]);
        } else {
            selectedOptions.splice(selectedOptions.indexOf(item.enumValue), 1);
            selectedKeys.splice(selectedKeys.indexOf(item.key), 1);
            setSelectedKeys([...selectedKeys]);
            
            if (selectedOptions.length > 0) {
                setSelectedOptions([...selectedOptions]);
            } else {
                setSelectedOptions([]);
            }
        }
    };

    const onNumberTextfieldChange = (values: NumberFormatValues) => {
        if (values.formattedValue.length > 0) {
            setPickerOption(values.floatValue);
        } else {
            setPickerOption(null);
        }
    };

    const onCssTextfieldChange = (values: NumberFormatValues) => {
        if (values.formattedValue.length > 0) {
            setPickerOption(values.formattedValue);
        } else {
            setPickerOption(null);
        }
    };

    const onBoolDropdownChange = (event: React.FormEvent<HTMLDivElement>, option?: IOptionProps, index?: number): void => {
        setPickerOption(option.boolValue);
    };

    useEffect(() => {
        if (selectedOptions !== null && selectedOptions.length > 0) {
            setPickerOption(selectedOptions as (string | number | Date | string[]));
        } else {
            setPickerOption(null);
        }
    }, [selectedOptions]);

    const handlePickerComponent = () => {
        switch (fieldOption) {
            case 'Status':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            className="lb-multi-picker"
                            onChange={onMultiSelectChange}
                            placeholder="Status"
                            styles={pickerDropdownStyles}
                            options={picker}
                            selectedKeys={selectedKeys as string[] | number[]}
                            calloutProps={{
                                className: "lb-multi-picker-callout"
                            }}
                            multiSelect
                        />
                    </div>
                );
            case 'Title':
                return (
                    <div className="picker-textfield-cont">
                        <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                    </div>
                );
            case 'Created On':
                return (
                    <DatePicker
                        className="datePickerStyles"
                        onSelectDate={(date) => {
                            setPickerOption(date);
                        }}
                        value={pickerOption !== null ? new Date(pickerOption as Date) : null}
                    />
                );
            case 'Submitted By':
                return (
                    <div className="picker-textfield-cont">
                        <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                    </div>
                );
            case 'Assigned To':
                return (
                    <div className="picker-search-box">
                        <AssignedToPicker
                            selectedKeys={selectedKeys as IUserProps[]}
                            setSelectedKeys={setSelectedKeys}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            multiSelectData={multiSelectData as (GraphUserDto | SubmitterDto)[]}
                            inEditMode={inEditMode}
                            isSubmitterPicker={false}
                        />
                    </div>
                );
            case 'Submitter':
                return (
                    <div className="picker-search-box">
                        <AssignedToPicker
                            selectedKeys={selectedKeys as IUserProps[]}
                            setSelectedKeys={setSelectedKeys}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            multiSelectData={multiSelectData as (GraphUserDto | SubmitterDto)[]}
                            inEditMode={inEditMode}
                            isSubmitterPicker={true}
                        />
                    </div>
                );
            case 'Tags':
                return (
                    <div className="picker-search-box">
                        <TagsPicker
                            selectedKeys={selectedKeys as IOptionProps[]}
                            setSelectedKeys={setSelectedKeys}
                            setSelectedOptions={setSelectedOptions}
                            selectedOptions={selectedOptions as IOptionProps[]}
                            inEditMode={inEditMode}
                            multiSelectData={multiSelectData}
                        />
                    </div>
                );
            case 'Description':
                return (
                    <div className="picker-textfield-cont">
                        <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                    </div>
                );
            case 'Troubled Product':
                return (
                    <div className="picker-product-box">
                        <ProductDevicePicker
                            selectedKeys={selectedKeys as IOptionProps[]}
                            setSelectedKeys={setSelectedKeys}
                            selectedOptions={selectedOptions}
                            setSelectedOptions={setSelectedOptions}
                            multiSelectData={multiSelectData as (string | number)[]}
                            inEditMode={inEditMode}
                        />
                    </div>
                );
            case 'MSX Opportunity':
                return (
                    <div className="picker-textfield-cont">
                        {conditionSelection?.enumValue === ListBuilderCondition.State ? 
                            <Dropdown 
                                options={[{ key: 0, text: StringConstants.HAS_MSX_OPP }, { key: 1, text: StringConstants.NO_MSX_OPP }]}
                                styles={pickerDropdownStyles}
                                onChange={onTextDropdownChange}
                            />
                            :
                            <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                        }
                    </div>
                );
            case 'MSX Status':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            onChange={onTextDropdownChange}
                            styles={pickerDropdownStyles}
                            placeholder="Status"
                            options={picker}
                            selectedKey={(selectedKeys as (string | number)[])[0]}
                        />
                    </div>
                );
            case 'TPID':
                return (
                    <div className="picker-textfield-cont">
                        <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                    </div>
                );
            case 'End customer':
                return (
                    <div className="picker-textfield-cont">
                        <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                    </div>
                );
            case 'Customer country':
                return (
                    <div className="picker-search-box">
                        <CountryPicker
                            setSelectedOptions={setSelectedOptions}
                            multiSelectData={multiSelectData}
                            inEditMode={inEditMode}
                        />
                    </div>
                );
            case 'MSX revenue':
            case 'Total Revenue Risk':
                return (
                    <div className="numberFormatWrapper">
                        <NumberFormat
                            thousandSeparator={true}
                            value={pickerOption === null ? '' : pickerOption as string | number}
                            onValueChange={onNumberTextfieldChange}
                            placeholder="$"
                            allowNegative={false}
                            inputMode="numeric"
                            className="num-format"
                            decimalScale={2}
                        />
                    </div>
                );
            case 'Customer Count':
            case 'CXP Priority Number':
            case 'MSX devices':
            case 'Total Device Risk':
            case 'DCF Value':
                return (
                    <div className="numberFormatWrapper">
                        <NumberFormat
                            thousandSeparator={true}
                            value={pickerOption === null ? '' : pickerOption as string | number}
                            onValueChange={onNumberTextfieldChange}
                            allowNegative={false}
                            inputMode="numeric"
                            className="num-format"
                            decimalScale={2}
                        />
                    </div>
                );
            case 'Relationship':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            onChange={onMultiSelectChange}
                            placeholder="Relationship"
                            styles={pickerDropdownStyles}
                            options={picker}
                            selectedKeys={selectedKeys as string[] | number[]}
                            calloutProps={{
                                className: "lb-multi-picker-callout"
                            }}
                            multiSelect
                        />
                    </div>
                );
            case 'Attachment':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            placeholder="State"
                            styles={pickerDropdownStyles}
                            onChange={onChange}
                            options={picker}
                            selectedKey={(selectedKeys as (string | number)[])[0]}
                        />
                    </div>
                );
            case 'Is DFS (design for surface) possible':
                return <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />;
            case 'Bug Type':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            placeholder="Bug"
                            styles={pickerDropdownStyles}
                            options={picker}
                            selectedKeys={selectedKeys as string[] | number[]}
                            onChange={onMultiSelectChange}
                            calloutProps={{
                                className: "lb-multi-picker-callout"
                            }}
                            multiSelect
                        />
                    </div>
                );
            case 'Aging':
                return (
                    <AgingPicker
                        setSelectedOptions={setSelectedOptions}
                        multiSelectData={multiSelectData as Aging[]}
                        inEditMode={inEditMode}
                        setSelectedKeys={setSelectedKeys}
                        selectedKeys={selectedKeys as IOptionProps[]}
                    />
                );
            case 'Bug Number':
                return (
                    <div className="picker-textfield-cont">
                        <TextField styles={pickerTextFieldStyles} onChange={onTextfieldChange} value={pickerOption as string} />
                    </div>
                );
            case 'CSS case number':
                return (
                    <div className="numberFormatWrapper">
                        {conditionSelection?.enumValue === ListBuilderCondition.State ? 
                            <Dropdown 
                                options={[{ key: 0, text: StringConstants.HAS_CSS_CASE }, { key: 1, text: StringConstants.NO_CSS_CASE }]}
                                styles={pickerDropdownStyles}
                                onChange={onTextDropdownChange}
                            />
                            :
                            <NumberFormat
                                value={pickerOption === null ? '' : pickerOption as string | number}
                                onValueChange={onCssTextfieldChange}
                                allowNegative={false}
                                inputMode="numeric"
                                className="num-format"
                            />
                        }
                    </div>
                );
            case 'Is This a DCF Request':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            placeholder="Yes/No"
                            styles={pickerDropdownStyles}
                            options={picker}
                            onChange={onBoolDropdownChange}
                            selectedKey={(selectedKeys as (string | number)[])[0]}
                        />
                    </div>
                );
            case 'Proof of purchase attached':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            placeholder="Yes/No"
                            styles={pickerDropdownStyles}
                            options={picker}
                            onChange={onBoolDropdownChange}
                            selectedKey={(selectedKeys as (string | number)[])[0]}
                        />
                    </div>
                );
            case 'Priority':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            placeholder="Priority"
                            onChange={onMultiSelectChange}
                            styles={pickerDropdownStyles}
                            options={picker}
                            selectedKeys={selectedKeys as string[] | number[]}
                            calloutProps={{
                                className: "lb-multi-picker-callout"
                            }}
                            multiSelect
                        />
                    </div>
                );
            case 'Serial number attached':
                return (
                    <div className="picker-dropdown-cont">
                        <Dropdown
                            placeholder="Yes/No"
                            styles={pickerDropdownStyles}
                            options={picker}
                            onChange={onBoolDropdownChange}
                            selectedKey={(selectedKeys as (string | number)[])[0]}
                        />
                    </div>
                );
            default:
                return <div>this</div>;
        }
    };

    return (
        <div>
            <div className={`${!pickerDropdownDisabled ? `${pickerActive ? 'field-active' : ''}` : 'this-field-text'}`}>
                {!pickerDropdownDisabled ? 
                    handlePickerComponent()
                    :
                    (<div>this</div>)
                }
            </div>
        </div>
    );
};

export default Picker;
